import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TbChartLine } from 'react-icons/tb';

import { financialOverviewQueryOptions } from '@/queries';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';

import { getDateRange } from '../../configuration';
import {
    FinancialOverviewChartDetailProps,
    FinancialOverviewChartEvent,
    FinancialOverviewTickerDataPoint,
} from '../../types';
import { FinancialOverviewAreaChart } from '../FinancialOverviewAreaChart';
import { buildFinancialOverviewChartData, resolveFinancialOverviewChartEvents } from './chartData';

const RECESSION_EVENTS: FinancialOverviewChartEvent[] = [
    { date: '1969-12-01', endDate: '1970-11-30', label: 'Recession', source: 'NBER', type: 'recessions' },
    { date: '1973-11-01', endDate: '1975-03-31', label: 'Recession', source: 'NBER', type: 'recessions' },
    { date: '1980-01-01', endDate: '1980-07-31', label: 'Recession', source: 'NBER', type: 'recessions' },
    { date: '1981-07-01', endDate: '1982-11-30', label: 'Recession', source: 'NBER', type: 'recessions' },
    { date: '1990-07-01', endDate: '1991-03-31', label: 'Recession', source: 'NBER', type: 'recessions' },
    { date: '2001-03-01', endDate: '2001-11-30', label: 'Recession', source: 'NBER', type: 'recessions' },
    { date: '2007-12-01', endDate: '2009-06-30', label: 'Recession', source: 'NBER', type: 'recessions' },
    { date: '2020-02-01', endDate: '2020-04-30', label: 'Recession', source: 'NBER', type: 'recessions' },
];

const EMPTY_TICKER_DATA: FinancialOverviewTickerDataPoint[] = [];

interface CachedComparisonData {
    data: FinancialOverviewTickerDataPoint[];
    rangeKey: string;
}

export const FinancialOverviewChartDetail: FC<FinancialOverviewChartDetailProps> = ({
    aspect,
    chartRange,
    chartType,
    comparisonCompanies,
    keyEvents,
    metric,
    setTickerData,
    showVolume,
    tickerSymbol,
}) => {
    const queryClient = useQueryClient();
    const [comparisonData, setComparisonData] = useState<Record<string, CachedComparisonData>>({});
    const range = useMemo(() => getDateRange(chartRange), [chartRange]);
    const rangeKey = `${chartRange}:${range.from}:${range.to}`;
    const comparisonSymbols = useMemo(
        () => comparisonCompanies.map((company) => company.symbol),
        [comparisonCompanies],
    );
    const normalizedTickerSymbol = tickerSymbol.trim().toUpperCase();
    const primaryQuery = useQuery({
        ...financialOverviewQueryOptions.marketSeries({
            chartRange,
            from: range.from,
            symbol: tickerSymbol,
            to: range.to,
        }),
        enabled: Boolean(tickerSymbol),
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey[3] === normalizedTickerSymbol ? previousData : undefined,
    });
    const newsQuery = useQuery({
        ...financialOverviewQueryOptions.news(tickerSymbol),
        enabled: Boolean(tickerSymbol && keyEvents.includes('news')),
    });
    const dividendsQuery = useQuery({
        ...financialOverviewQueryOptions.dividends(tickerSymbol),
        enabled: Boolean(tickerSymbol && keyEvents.includes('dividends')),
    });
    const tickerData = primaryQuery.data ?? EMPTY_TICKER_DATA;
    const loading = primaryQuery.isPending && !tickerData.length;

    useEffect(() => {
        setTickerData(tickerData);
    }, [setTickerData, tickerData]);

    useEffect(() => {
        if (!comparisonCompanies.length || chartRange === 'realTime') {
            return;
        }

        let active = true;

        const loadComparisons = async () => {
            const results = await Promise.allSettled(
                comparisonCompanies.map(async (company) => {
                    const data = await queryClient.fetchQuery(
                        financialOverviewQueryOptions.marketSeries({
                            chartRange,
                            from: range.from,
                            symbol: company.symbol,
                            to: range.to,
                        }),
                    );

                    return [company.symbol, { data, rangeKey }] as const;
                }),
            );

            if (!active) {
                return;
            }

            const loadedData = Object.fromEntries(
                results
                    .filter(
                        (result): result is PromiseFulfilledResult<readonly [string, CachedComparisonData]> =>
                            result.status === 'fulfilled',
                    )
                    .map((result) => result.value),
            );

            setComparisonData((current) => ({ ...current, ...loadedData }));
        };

        void loadComparisons();

        return () => {
            active = false;
        };
    }, [chartRange, comparisonCompanies, queryClient, range.from, range.to, rangeKey]);

    const activeComparisonData = useMemo(
        () =>
            Object.fromEntries(
                comparisonSymbols.flatMap((symbol) => {
                    const cached = comparisonData[symbol];

                    return cached?.rangeKey === rangeKey ? [[symbol, cached.data] as const] : [];
                }),
            ),
        [comparisonData, comparisonSymbols, rangeKey],
    );

    const data = useMemo(
        () => buildFinancialOverviewChartData(tickerData, activeComparisonData, comparisonSymbols, metric),
        [activeComparisonData, comparisonSymbols, metric, tickerData],
    );
    const events = useMemo(
        () => [
            ...(keyEvents.includes('recessions')
                ? RECESSION_EVENTS.filter(
                      (event) => event.date <= range.to && (event.endDate ?? event.date) >= range.from,
                  )
                : []),
            ...(keyEvents.includes('news')
                ? (newsQuery.data ?? [])
                      .filter((event) => event.date >= range.from && event.date <= range.to)
                      .slice(0, 60)
                : []),
            ...(keyEvents.includes('dividends')
                ? (dividendsQuery.data ?? []).filter((event) => event.date >= range.from && event.date <= range.to)
                : []),
        ],
        [dividendsQuery.data, keyEvents, newsQuery.data, range.from, range.to],
    );
    const resolvedEvents = useMemo(() => resolveFinancialOverviewChartEvents(events, data), [data, events]);
    const chartTypeMap = {
        area: (
            <FinancialOverviewAreaChart
                aspect={aspect}
                chartRange={chartRange}
                comparisonSeries={comparisonCompanies.map((company) => ({
                    dataKey: `comparison:${company.symbol}`,
                    label: company.companyName,
                    symbol: company.symbol,
                }))}
                data={data}
                events={resolvedEvents}
                metric={metric}
                showVolume={showVolume && metric !== 'volume'}
                tickerSymbol={tickerSymbol}
            />
        ),
    } as Record<string, ReactNode>;

    return (
        <div style={{ marginTop: '0.75rem', minWidth: 0, overflowX: 'hidden' }}>
            {loading ? (
                <BaseSkeleton paragraph={{ rows: 12 }} />
            ) : data.length ? (
                chartTypeMap[chartType]
            ) : (
                <EmptySection
                    description='No market data is available for the selected metric and date range.'
                    height='18rem'
                    icon={<TbChartLine size='1.25rem' />}
                    label='Market Performance Unavailable'
                />
            )}
        </div>
    );
};
